// Use the environment variable or default to the current origin for API requests
const API_BASE_URL =
    typeof window !== 'undefined'
        ? process.env.NEXT_PUBLIC_API_URL || window.location.origin
        : process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

/**
 * Check if a file is a valid PDF
 * @param file File to check
 * @returns Promise<boolean> True if the file is a valid PDF
 */
export async function isPdfFile(file: File): Promise<boolean> {
    if (!file) return false;

    // Check file type and extension
    const isPdfByType = file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');

    // Additional check for PDF magic numbers for better reliability
    if (isPdfByType && file.size > 4) {
        try {
            const buffer = await file.slice(0, 4).arrayBuffer();
            const uint8Array = new Uint8Array(buffer);
            // Check for PDF magic number: %PDF
            return uint8Array[0] === 0x25 && // %
                uint8Array[1] === 0x50 && // P
                uint8Array[2] === 0x44 && // D
                uint8Array[3] === 0x46;   // F
        } catch (error) {
            console.warn('Error checking PDF signature:', error);
            return isPdfByType;
        }
    }

    return isPdfByType;
}

/**
 * Extract text from a PDF file using the backend API
 * @param file PDF file to process
 * @returns Promise<string> Extracted text from the PDF
 */
export async function extractTextFromPdf(file: File): Promise<string> {
    if (!file) {
        throw new Error('No file provided');
    }

    // Validate file type
    if (!(await isPdfFile(file))) {
        throw new Error('Please upload a valid PDF file');
    }

    // Check file size (2MB limit)
    const MAX_FILE_SIZE_MB = 2;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        throw new Error(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
        // Remove any trailing slashes from the base URL
        const baseUrl = API_BASE_URL.replace(/\/$/, '');
        const response = await fetch(`${baseUrl}/api/process-pdf`, {
            method: 'POST',
            body: formData,
            // Don't set Content-Type header, let the browser set it with the correct boundary
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail ||
                errorData.message ||
                `Failed to process PDF (${response.status})`;

            // Handle specific error cases
            if (response.status === 400) {
                throw new Error(`Invalid request: ${errorMessage}`);
            } else if (response.status === 413) {
                throw new Error('File size exceeds the allowed limit');
            } else if (response.status >= 500) {
                throw new Error('Server error while processing PDF');
            } else {
                throw new Error(errorMessage);
            }
        }

        const data = await response.json();

        if (!data.text || typeof data.text !== 'string') {
            throw new Error('Invalid response from server');
        }

        return data.text;
    } catch (error) {
        console.error('Error in extractTextFromPdf:', error);

        // Handle network errors
        if (error instanceof TypeError) {
            throw new Error('Network error. Please check your connection and try again.');
        }

        // Re-throw with a more user-friendly message if it's not already an Error
        throw error instanceof Error
            ? error
            : new Error('Failed to process PDF. Please try again or use a different file.');
    }
}
